import { getSheetSize } from "../domain/sheetSize";

export function rectsOverlap(a, b) {
  return (
    a.x < b.x + b.length &&
    a.x + a.length > b.x &&
    a.y < b.y + b.width &&
    a.y + a.width > b.y
  );
}

export function normalizeRect(rect) {
  return {
    ...rect,
    x: Math.round(rect.x),
    y: Math.round(rect.y),
    length: Math.round(rect.length),
    width: Math.round(rect.width),
  };
}

export function isUsableOffcut(candidate, minLength, minWidth) {
  return (
    Math.max(candidate.length, candidate.width) >= minLength &&
    Math.min(candidate.length, candidate.width) >= minWidth
  );
}

export function getFreeCells(sheet, sourcePanel) {
  const { length: boardLength, width: boardWidth } = getSheetSize(sheet, sourcePanel);
  const placements = (sheet.placements || []).map((placement) => ({
    x: Number(placement.x),
    y: Number(placement.y),
    length: Number(placement.length),
    width: Number(placement.width),
    name: placement.name,
  }));

  if (!placements.length) {
    return {
      boardLength,
      boardWidth,
      placements,
      cells: [{ x: 0, y: 0, length: boardLength, width: boardWidth }],
    };
  }

  const xCuts = [...new Set([0, boardLength, ...placements.flatMap((placement) => [placement.x, placement.x + placement.length])])]
    .filter((value) => value >= 0 && value <= boardLength)
    .sort((a, b) => a - b);

  const yCuts = [...new Set([0, boardWidth, ...placements.flatMap((placement) => [placement.y, placement.y + placement.width])])]
    .filter((value) => value >= 0 && value <= boardWidth)
    .sort((a, b) => a - b);

  const cells = [];
  for (let xIndex = 0; xIndex < xCuts.length - 1; xIndex += 1) {
    for (let yIndex = 0; yIndex < yCuts.length - 1; yIndex += 1) {
      const cell = {
        x: xCuts[xIndex],
        y: yCuts[yIndex],
        length: xCuts[xIndex + 1] - xCuts[xIndex],
        width: yCuts[yIndex + 1] - yCuts[yIndex],
      };

      if (cell.length <= 0 || cell.width <= 0) continue;
      if (placements.some((placement) => rectsOverlap(cell, placement))) continue;

      cells.push(cell);
    }
  }

  return { boardLength, boardWidth, placements, cells, xCuts, yCuts };
}

export function mergeFreeCells(cells, xCuts = [], yCuts = []) {
  const candidates = [];

  yCuts.slice(0, -1).forEach((y) => {
    const rowCells = cells
      .filter((cell) => cell.y === y)
      .sort((a, b) => a.x - b.x);

    let current = null;
    rowCells.forEach((cell) => {
      if (!current) {
        current = { ...cell };
        return;
      }

      if (current.x + current.length === cell.x && current.width === cell.width) {
        current.length += cell.length;
      } else {
        candidates.push(current);
        current = { ...cell };
      }
    });
    if (current) candidates.push(current);
  });

  xCuts.slice(0, -1).forEach((x) => {
    const columnCells = cells
      .filter((cell) => cell.x === x)
      .sort((a, b) => a.y - b.y);

    let current = null;
    columnCells.forEach((cell) => {
      if (!current) {
        current = { ...cell };
        return;
      }

      if (current.y + current.width === cell.y && current.length === cell.length) {
        current.width += cell.width;
      } else {
        candidates.push(current);
        current = { ...cell };
      }
    });
    if (current) candidates.push(current);
  });

  return candidates;
}

export function getPotentialOffcuts(sheet, sourcePanel, minLength, minWidth, mode = "merged") {
  const { placements, cells, xCuts = [], yCuts = [] } = getFreeCells(sheet, sourcePanel);

  if (!placements.length) {
    return cells
      .map((cell) => ({ ...normalizeRect(cell), label: "volledige plaat" }))
      .filter((cell) => isUsableOffcut(cell, minLength, minWidth));
  }

  const candidates = mode === "split"
    ? cells
    : mergeFreeCells(cells, xCuts, yCuts);

  const deduped = new Map();
  candidates
    .map((candidate) => normalizeRect(candidate))
    .filter((candidate) => isUsableOffcut(candidate, minLength, minWidth))
    .filter((candidate) => !placements.some((placement) => rectsOverlap(candidate, placement)))
    .forEach((candidate) => {
      const key = `${candidate.x}-${candidate.y}-${candidate.length}-${candidate.width}`;
      const label = mode === "split"
        ? "los zaagrest"
        : candidate.x > 0 && candidate.y === 0
          ? "rechter restvlak"
          : candidate.y > 0 && candidate.x === 0
            ? "onderste restvlak"
            : "vrij restvlak";

      deduped.set(key, { ...candidate, label });
    });

  return [...deduped.values()]
    .sort((a, b) => b.length * b.width - a.length * a.width)
    .slice(0, 5);
}
